import {ApplicationTypes, BoolLike, BuildType, DebtTypes, MortgageTypes} from "../../../../../../../../models";
import {Figure} from "../Figure";
import {estimateSellingCosts} from "./estimateSellingCosts";

export function funds(mortgage) {


    function gifts() {
        if (!mortgage.requirement.receivingGifts) {
            return null;
        }
        const fig = new Figure({
            label: 'Gifts',
            disabled: !mortgage.requirement.receivingGifts,
            namesPath: ['funds', 'gifts'],
            provable: true,
            float: false,
            source: null,
        })
        const section = mortgage.uploadSections.find(it => it.name === 'gift-certificate');
        const uploads = section?.verifiableUploads?.length ? section.verifiableUploads : [];
        if (uploads.length) {
            uploads.forEach((u, i) => {
                fig.addMethod({
                    description: `Gift from ${u.values.gifterNames}`,
                    claimed: i === 0 ? mortgage.requirement.depositGifts : null,
                    amount: u.values.amount || 0,
                    uploadID: u.id,
                    operator: '+',
                    warning: !u.values.amount ? `Amount missing in upload` : null
                })
            })
            const total = fig.methodology.reduce((acc, row) => acc + (row.amount || 0), 0)
            fig.setValue(total, 'Gift Certificates in Uploads')
        } else {
            fig.setAlert('No gift cert uploads')
            fig.setValue(mortgage.requirement.receivingGifts ? mortgage.requirement.depositGifts : 0, 'Claimed Gift Amount')
        }
        return fig;
    }

    function savings() {
        const {currencyAccounts} = mortgage.accountSummary();
        const investmentSections = mortgage.uploadSections.filter(it => it.isActive && it.stage === 'APPLY' && it.props.isInvestment )

        // Investments are included in the savings figure; keep their euro total separately so the
        // Balance of Funds can show cash and investments apart
        let investmentsTotal = 0
        investmentSections.forEach(section => {
            const currency = section?.data?.currency
            const isEuro = !currency || currency.toLowerCase().trim().startsWith('eur') || currency.trim() === '€'
            const myCurrency = isEuro ? '€' : currency;
            if (!currencyAccounts[myCurrency]) {
                currencyAccounts[myCurrency] = {accounts: []}
                if (!isEuro) {
                    currencyAccounts[myCurrency].accountingFx = mortgage.requirement.accountingFx[myCurrency] ? mortgage.requirement.accountingFx[myCurrency] : 1
                }
            }
            // newest balance for investments (personalTitle, mostRecentClosing, uploadID)
            const mostRecentClosing = section.mostRecentClosingBalance
            if (mostRecentClosing) {
                currencyAccounts[myCurrency].accounts.push({
                    personalTitle: section.personalTitle || 'Investment Account',
                    closingOnDate: {
                        balance: mostRecentClosing.balance,
                        uploadID: mostRecentClosing.uploadID
                    },
                    // match bank accounts from financesInRange: fx rate for foreign currencies, null for euro
                    convertRate: isEuro ? null : currencyAccounts[myCurrency].accountingFx
                })
                currencyAccounts[myCurrency].totalBalance = (currencyAccounts[myCurrency].totalBalance || 0) + mostRecentClosing.balance
                const investmentRate = isEuro ? 1 : Number(currencyAccounts[myCurrency].accountingFx || 1)
                investmentsTotal += Number(((mostRecentClosing.balance || 0) / investmentRate).toFixed(2))
                if (!isEuro) {
                    const rate = currencyAccounts[myCurrency].accountingFx ? Number(currencyAccounts[myCurrency].accountingFx) : 1
                    currencyAccounts[myCurrency].convertedTotalBalance = Number((currencyAccounts[myCurrency].totalBalance / rate).toFixed(2))
                }
            }

        })
        const fig = new Figure({
            label: 'Cash Available',
            disabled: false,
            namesPath: ['funds', 'savings'],
            provable: true,
            float: false,
        })
        fig.investmentsTotal = investmentsTotal
        let bigTotal = 0
        Object.keys(currencyAccounts).forEach(currency=> {
            const rate = currencyAccounts[currency]?.accountingFx ? Number (currencyAccounts[currency]?.accountingFx) : null
            //const balance = currencyAccounts[currency]?.totalBalance || 0
            //const convertedBalance = balance / rate ?? 1
            const convertedBalance = currencyAccounts[currency].convertedTotalBalance ?? currencyAccounts[currency]?.totalBalance ?? 0

            const totalBalance = Number(convertedBalance.toFixed(2))
            bigTotal += totalBalance
            currencyAccounts[currency].accounts.forEach((account, i) => {

                    let description = account.personalTitle
                    let origAmount = account.closingOnDate?.balance || 0
                    if (rate) {
                        description += ` (${origAmount} fx@${rate})`
                    }
                    let amount = rate ? Number((origAmount/rate).toFixed(2)) : origAmount;
                    fig.addMethod({
                        description,
                        amount,
                        uploadID: account.closingOnDate?.uploadID || null,
                        operator: '+',
                        warning: !account.closingOnDate?.balance ? `No verified balance` : null
                    })


            })
        })



        if (bigTotal > 0) {
            bigTotal = Number(bigTotal.toFixed(2))
            fig.setValue(bigTotal, 'Verified Cash in Banks')
            return fig
        }





        // const bigTotal = Number(currencyAccounts['€']?.totalBalance.toFixed(0))
        // if (bigTotal > 0) {
        //     currencyAccounts['€'].accounts.forEach(acc => {
        //         fig.addMethod({
        //             description: acc.personalTitle,
        //             amount: acc.closingOnDate?.balance || 0,
        //             uploadID: acc.closingOnDate?.uploadID || null,
        //             operator: '+',
        //             warning: !acc.closingOnDate?.balance ? `No verified balance` : null
        //         })
        //     })
        //     fig.setValue(bigTotal, 'Verified Cash in Banks')
        //     return fig
        // }
        fig.setAlert('Claimed cash in banks used')
        fig.setValue(mortgage.requirement.depositSavings, 'Claimed Cash in Banks')
        return fig

    }

    function profitFromSale() {
        const properties = mortgage.twoApplicants ? [...(mortgage.applicant1.properties || []), ...(mortgage.applicant2.properties || [])] : mortgage.applicant1.properties || []
        const sellingProperties = properties.filter(it => it.toBeSold)

        const fig = new Figure({
            label: 'Profit From Sale',
            disabled: sellingProperties.length === 0,
            namesPath: ['funds', 'profitFromSale'],
            provable: true,
            float: false,
        })

        let totalProfit = 0;
        sellingProperties.forEach(prop => {
            // Irish selling costs are only estimated for euro properties
            const currency = prop.currency;
            const isEuro = !currency || currency.trim() === '€' || currency.toLowerCase().trim().startsWith('eur');
            const sellingCosts = isEuro ? estimateSellingCosts(prop.estimatedValue || 0).total : 0;
            // a stale outstanding balance is ignored when the property isn't mortgaged
            const outstanding = prop.isMortgaged ? (prop.outstanding || 0) : 0;
            const profit = (prop.estimatedValue || 0) - outstanding - sellingCosts;
            totalProfit += profit;
            // is there a verified upload for estimated value?
            fig.addMethod({
                description: prop.address || 'Property',
                help: `Estimated Value (${prop.estimatedValue || 0}) - Outstanding Mortgage (${outstanding}) - Estimated Selling Costs (${sellingCosts})`,
                claimed: profit,
                amount: 0,
                uploadID: null,
                operator: '+'
            })
        })
        fig.setAlert('No sale price uploads')
        fig.setValue(sellingProperties.length === 0 ? null : totalProfit, 'Claimed profit from sale')
        return fig;

    }

    function contractDepositPaid() {
        const fig = new Figure({
            label: 'Contract Deposit Paid',
            disabled: !mortgage.property.contractDepositPaid,
            namesPath: ['funds', 'contractDepositPaid'],
            provable: true,
            float: false,
        })
        fig.setAlert('No contract deposit uploads')
        fig.setValue(mortgage.property.contractDepositPaid, 'Claimed contract deposit paid')
        return fig
    }

    function bookingDepositPaid() {
        const fig = new Figure({
            label: 'Booking Deposit Paid',
            disabled: !mortgage.property.bookingDepositPaid,
            namesPath: ['funds', 'bookingDepositPaid'],
            provable: true,
            float: false,
        })
        const section = mortgage.uploadSections.find(it => it.name === 'deposit-receipt');
        const uploads = section?.verifiableUploads?.length ? section.verifiableUploads : [];

        if (uploads.length) {
            uploads.forEach(u => {
                fig.addMethod({
                    description: `Booking Deposit Receipt`,
                    amount: u.values.amount || 0,
                    uploadID: u.id,
                    operator: '+',
                    warning: !u.values.amount ? `Amount missing in upload` : null
                })
            })
            const total = fig.methodology.reduce((acc, row) => acc + (row.amount || 0), 0)
            fig.setValue(total, 'Verified booking deposit')
        } else {
            fig.setAlert('No booking deposit uploads')
            fig.setValue(mortgage.property.bookingDepositPaid, 'Claimed booking deposit')
        }
        return fig
    }

    function helpToBuy() {
        if (mortgage.requirement.helpToBuyScheme === BoolLike.FALSE){
            return null
        }
        const fig = new Figure({
            label: 'Help To Buy Scheme',
            disabled: mortgage.property.buildType === BuildType.SECONDHAND || mortgage.applicationType !== 'FIRSTTIME' || mortgage.requirement.helpToBuyScheme === BoolLike.FALSE,
            namesPath: ['funds', 'helpToBuy'],
            provable: true,
            float: false,
        })
        const section = mortgage.uploadSections.find(it => it.name === 'htb-scheme-forms' );
        const haveDocument = section?.verifiableUploads?.length > 0;
        if (haveDocument) {

            fig.addMethod({
                description: `HTB Scheme Document`,
                claimed:  mortgage.requirement.helpToBuySchemeAmount ,
                amount: mortgage.requirement.helpToBuySchemeAmount,
                uploadID: section?.verifiableUploads[0].id,
                operator: '=',
            })
            fig.setValue(mortgage.requirement.helpToBuySchemeAmount, 'Verified HTB document')
        } else {
            fig.setAlert('No HTB scheme uploads')
            if (!fig.disabled && !mortgage.requirement.helpToBuySchemeAmount) {
                let amt = mortgage.twoApplicants ? 30000 : 25000
                fig.setValue(amt, 'Estimated HTB scheme amount')
            } else {
                fig.setValue(mortgage.requirement.helpToBuySchemeAmount, 'Claimed HTB scheme amount')
            }

        }
        return fig;
    }


    function firstHome() {
        if (mortgage.requirement.firstHomeScheme  === BoolLike.FALSE) {
            return null
        }
        const fig = new Figure({
            label: 'First Home Scheme',
            disabled: !(mortgage.mortgageType === MortgageTypes.PURCHASE && mortgage.applicationType === ApplicationTypes.FIRSTTIME && mortgage.requirement.firstHomeScheme === BoolLike.TRUE),
            namesPath: ['funds', 'firstHome'],
            provable: true,
            float: false
        })
        fig.setAlert('No First Home scheme uploads')
        fig.setValue(mortgage.requirement.firstHomeSchemeAmount, 'Claimed First Home scheme amount')
        return fig
    }

    function loanClearance() {
        const allDebts = [
            ...(mortgage.applicant1?.debts || []),
            ...(mortgage.twoApplicants ? (mortgage.applicant2?.debts || []) : [])
        ]
        const clearingLoanIds = allDebts.filter(it => it.debtType === DebtTypes.LOAN && it.clearedBeforeMortgage).map(it => it.id)
        const sections = (mortgage.uploadSections || []).filter(it => clearingLoanIds.includes(it.index))
        const fig = new Figure({
            label: 'Loan(s) to clear before drawdown',
            disabled: clearingLoanIds.length === 0,
            namesPath: ['funds', 'loanClearance'],
            provable: true,
            float: false,
        })

        if (fig.disabled) {
            fig.setValue(0, 'No loans selected for clearance')
            return fig
        }

        let provableTotal = 0
        sections.forEach(section => {
            const os = section?.outstandingBalanceUpload

            const amount = os?.amount ?? null
            if (amount != null) {
                provableTotal += Number(amount)
                console.log({os, provableTotal})
                fig.addMethod({
                    description: section.personalTitle || section.data?.lender || 'Loan Statement',
                    amount: Number(amount),
                    claimed: null,
                    uploadID: os?.uploadID || null,
                    operator: '+',
                })
            }
        })

        if (provableTotal !== 0) {
            fig.setValue(Number(provableTotal.toFixed(2)), 'Latest outstanding loan statement balances')
            console.log({fig})
            return fig
        }

        const claimedTotal = allDebts
            .filter(it => clearingLoanIds.includes(it.id))
            .reduce((acc, debt) => acc + Math.abs(Number(debt.outstanding || 0)), 0)
        fig.setAlert('No loan statement balances available')
        fig.setValue(-1 * Number(claimedTotal.toFixed(2)), 'Claimed outstanding balances')

        return fig
    }

    return [
        gifts(),
        savings(),
        profitFromSale(),
        bookingDepositPaid(),
        contractDepositPaid(),
        firstHome(),
        helpToBuy(),
        loanClearance()
    ].filter(it => it != null);
}